import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { useSelector } from 'react-redux';
import { DatePicker, Button } from '@bwoty-web/styleguide';
import { isSmallScreen } from '../../../shared/utils/isSmallScreen';
import LargePopup from '../LargePopup/LargePopup';
import { NoSSR } from '../../../shared/components/NoSSR';

import styles from './DateFieldOverlay.scss';

const startDate = format(new Date(), 'yyyy-MM-dd');

export const DateFieldOverlay = ({
  isOpen = false,
  title,
  availableDates,
  selected,
  submitLabel,
  onClose,
  onChange,
  dateFrom,
}) => {
  const [currentSelected, setSelected] = useState(selected ? selected.value : null);
  const fromDateAsDateObj = dateFrom ? new Date(dateFrom.value) : null;
  const { siteId } = useSelector((state) => state.settings);

  const handleConfirm = useCallback(() => {
    onChange(currentSelected);
    onClose();
  }, [currentSelected, onChange]);

  const handleSelect = useCallback(
    (d) => {
      if (isSmallScreen()) {
        // select the value, don't close or trigger change
        setSelected(d);
      } else {
        // close and trigger change
        onChange(d);
        onClose();
      }
    },
    [onChange, onClose],
  );

  useEffect(() => {
    setSelected(selected ? selected.value : null);
  }, [isOpen]);

  const validDates = availableDates
    .filter((day) => {
      if (!fromDateAsDateObj) {
        return true;
      }

      return differenceInCalendarDays(new Date(day.value), fromDateAsDateObj) >= 0;
    })
    .map((x) => x.value);

  return (
    <LargePopup header={title} onClose={onClose} isOpen={isOpen}>
      <div className={styles.root}>
        <div className={styles.body}>
          <div className={styles.content}>
            <div className={styles.picker}>
              <NoSSR>
                <DatePicker
                  siteId={siteId}
                  startDate={startDate}
                  nrOfMonths={4}
                  key={isOpen ? 'datepicker' : 'hidden-picker'}
                  onChange={handleSelect}
                  selectedDate={currentSelected}
                  validDates={validDates}
                />
              </NoSSR>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button theme="primary" className={styles.button} onClick={handleConfirm}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </LargePopup>
  );
};

export const DateShape = PropTypes.shape({ display: PropTypes.string, value: PropTypes.string });

DateFieldOverlay.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.node,
  submitLabel: PropTypes.node,
  availableDates: PropTypes.arrayOf(DateShape),
  selected: DateShape,
  dateFrom: DateShape,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};
